import React, { useState, useMemo } from 'react'

import { useQuery } from '@apollo/client'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import styles from './RecommendationDataBrowser.module.scss'
import { RecommendationDataDocument } from '../../../__generated__/graphql'
import { client } from '../../../apollo'

interface RecommendationData {
  spec: {
    recommendationTitle: string
    concernTags: string[]
    explanationText: { [key: string]: string } | string
    bodyTitle?: string
    settings?: { [key: string]: string }
    steps?: string[]
    customActionDescription?: string
    canUndo?: boolean
    isAutomated: boolean
    bonus?: boolean
    isTask?: boolean
    taskType?: string
    [key: string]: any
  }
  metadata?: {
    categories: Array<{
      category: string
      subcategory: string
    }>
    action_counts: {
      gather?: {
        n_clicks: number
        n_pages: number
        type: string
      }
      update?: {
        n_clicks: number
        n_pages: number
        type: string
      }
    }
  } | null
}

type RecommendationDataMap = { [key: string]: RecommendationData }

type CopyFormat = 'json' | 'yaml' | 'spec-only' | 'metadata-only'

export default function RecommendationDataBrowser() {
  const [selectedKey, setSelectedKey] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [copyFormat, setCopyFormat] = useState<CopyFormat>('json')
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')

  const {
    data: queryData,
    loading,
    error,
  } = useQuery(RecommendationDataDocument, {
    client,
  })

  // Transform GraphQL response to the expected format
  const data: RecommendationDataMap | null = useMemo(() => {
    if (!queryData?.recommendationData) return null

    const result: RecommendationDataMap = {}
    for (const item of queryData.recommendationData) {
      result[item.key] = {
        spec: JSON.parse(item.spec),
        metadata: item.metadata ? JSON.parse(item.metadata) : null,
      }
    }

    // Set initial selected key if not set
    if (!selectedKey && Object.keys(result).length > 0) {
      setSelectedKey(Object.keys(result)[0])
    }

    return result
  }, [queryData, selectedKey])

  const getFilteredKeys = (): string[] => {
    if (!data) return []
    const keys = Object.keys(data)
    if (!searchQuery) return keys

    const query = searchQuery.toLowerCase()
    return keys.filter((key) => {
      const rec = data[key]
      return (
        key.toLowerCase().includes(query) ||
        rec.spec.recommendationTitle.toLowerCase().includes(query) ||
        rec.spec.concernTags.some((tag) => tag.toLowerCase().includes(query))
      )
    })
  }

  const formatDataForCopy = (key: string, format: CopyFormat): string => {
    if (!data || !data[key]) return ''

    const rec = data[key]

    switch (format) {
      case 'json':
        return JSON.stringify({ [key]: rec }, null, 2)

      case 'yaml':
        // Simple YAML serialization
        return `${key}:\n  spec:\n${JSON.stringify(rec.spec, null, 2)
          .split('\n')
          .map((line) => `    ${line}`)
          .join('\n')}\n  metadata:\n${JSON.stringify(rec.metadata, null, 2)
          .split('\n')
          .map((line) => `    ${line}`)
          .join('\n')}`

      case 'spec-only':
        return JSON.stringify({ [key]: rec.spec }, null, 2)

      case 'metadata-only':
        return JSON.stringify({ [key]: rec.metadata }, null, 2)

      default:
        return JSON.stringify({ [key]: rec }, null, 2)
    }
  }

  const handleCopy = async () => {
    if (!selectedKey || !data) return

    try {
      const textToCopy = formatDataForCopy(selectedKey, copyFormat)
      await navigator.clipboard.writeText(textToCopy)
      setSnackbarMessage(
        `Copied ${selectedKey} as ${copyFormat.toUpperCase()}!`,
      )
      setSnackbarOpen(true)
    } catch (_err) {
      setSnackbarMessage('Failed to copy to clipboard')
      setSnackbarOpen(true)
    }
  }

  const handleCopyAll = async () => {
    if (!data) return

    try {
      let allData: any = data
      if (copyFormat === 'spec-only') {
        allData = Object.entries(data).reduce((acc, [key, rec]) => {
          acc[key] = rec.spec
          return acc
        }, {} as any)
      } else if (copyFormat === 'metadata-only') {
        allData = Object.entries(data).reduce((acc, [key, rec]) => {
          acc[key] = rec.metadata
          return acc
        }, {} as any)
      }

      const textToCopy = JSON.stringify(allData, null, 2)
      await navigator.clipboard.writeText(textToCopy)
      setSnackbarMessage(
        `Copied all ${Object.keys(data).length} recommendations!`,
      )
      setSnackbarOpen(true)
    } catch (_err) {
      setSnackbarMessage('Failed to copy to clipboard')
      setSnackbarOpen(true)
    }
  }

  const filteredKeys = getFilteredKeys()
  const selectedData = selectedKey && data ? data[selectedKey] : null

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>
  }

  return (
    <div className={styles.recommendationDataBrowser}>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Browse and copy recommendation data from YAML specs and impact metrics
        metadata. Total recommendations: {data ? Object.keys(data).length : 0}
      </Typography>

      <Stack spacing={2}>
        <TextField
          label="Search recommendations"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by key, title, or concern tag..."
          fullWidth
        />

        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 300, flex: 1 }}>
            <InputLabel>Select Recommendation</InputLabel>
            <Select
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
              label="Select Recommendation"
            >
              {filteredKeys.map((key) => (
                <MenuItem key={key} value={key}>
                  {key} - {data?.[key]?.spec.recommendationTitle}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Copy Format</InputLabel>
            <Select
              value={copyFormat}
              onChange={(e) => setCopyFormat(e.target.value as CopyFormat)}
              label="Copy Format"
            >
              <MenuItem value="json">JSON (Full)</MenuItem>
              <MenuItem value="spec-only">Spec Only</MenuItem>
              <MenuItem value="metadata-only">Metadata Only</MenuItem>
              <MenuItem value="yaml">YAML-like</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            disabled={!selectedKey}
          >
            Copy Selected
          </Button>

          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyAll}
            disabled={!data}
          >
            Copy All
          </Button>
        </Stack>

        {selectedData && (
          <Box className={styles.dataDisplay}>
            <Typography variant="h6" gutterBottom>
              {selectedKey}
            </Typography>

            <div className={styles.section}>
              <Typography variant="subtitle2" className={styles.sectionTitle}>
                Spec Data
              </Typography>
              <Box className={styles.dataSection}>
                <Typography variant="body2">
                  <strong>Title:</strong>{' '}
                  {selectedData.spec.recommendationTitle}
                </Typography>
                <Typography variant="body2">
                  <strong>Automated:</strong>{' '}
                  {selectedData.spec.isAutomated ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2">
                  <strong>Can Undo:</strong>{' '}
                  {selectedData.spec.canUndo ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2">
                  <strong>Bonus:</strong>{' '}
                  {selectedData.spec.bonus ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2">
                  <strong>Is Task:</strong>{' '}
                  {selectedData.spec.isTask ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2">
                  <strong>Concern Tags:</strong>{' '}
                  {selectedData.spec.concernTags.join(', ')}
                </Typography>
                {selectedData.spec.settings && (
                  <Typography variant="body2">
                    <strong>Settings Count:</strong>{' '}
                    {Object.keys(selectedData.spec.settings).length}
                  </Typography>
                )}
                {selectedData.spec.steps && (
                  <Typography variant="body2">
                    <strong>Steps Count:</strong>{' '}
                    {selectedData.spec.steps.length}
                  </Typography>
                )}
              </Box>
            </div>

            {selectedData.metadata && (
              <div className={styles.section}>
                <Typography variant="subtitle2" className={styles.sectionTitle}>
                  Impact Metrics Metadata
                </Typography>
                <Box className={styles.dataSection}>
                  {selectedData.metadata.categories.length > 0 && (
                    <Typography variant="body2">
                      <strong>Categories:</strong>
                      <ul>
                        {selectedData.metadata.categories.map((cat) => (
                          <li key={`${cat.category}-${cat.subcategory}`}>
                            {cat.category} → {cat.subcategory}
                          </li>
                        ))}
                      </ul>
                    </Typography>
                  )}
                  {selectedData.metadata.action_counts.gather && (
                    <Typography variant="body2">
                      <strong>Gather Actions:</strong>{' '}
                      {selectedData.metadata.action_counts.gather.n_clicks}{' '}
                      clicks,{' '}
                      {selectedData.metadata.action_counts.gather.n_pages} pages
                      ({selectedData.metadata.action_counts.gather.type})
                    </Typography>
                  )}
                  {selectedData.metadata.action_counts.update && (
                    <Typography variant="body2">
                      <strong>Update Actions:</strong>{' '}
                      {selectedData.metadata.action_counts.update.n_clicks}{' '}
                      clicks,{' '}
                      {selectedData.metadata.action_counts.update.n_pages} pages
                      ({selectedData.metadata.action_counts.update.type})
                    </Typography>
                  )}
                </Box>
              </div>
            )}

            <div className={styles.section}>
              <Typography variant="subtitle2" className={styles.sectionTitle}>
                Raw Data Preview
              </Typography>
              <pre className={styles.codeBlock}>
                {formatDataForCopy(selectedKey, copyFormat)}
              </pre>
            </div>
          </Box>
        )}
      </Stack>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </div>
  )
}
